import { Ratings, Splits } from '../entities';

const BuyRating = ['Buy', 'Conviction Buy', 'Market Outperform', 'Outperform', 'Strong Buy'];
const OverweightRating = ['Overweight'];
const HoldRating = ['Hold', 'Neutral'];
const UnderweightRating = ['Underweight'];
const SellRating = ['Sell', 'Underperform'];
type RatingsAggregate = {
  Buy: number;
  Hold: number;
  Overweight: number;
  Sell: number;
  Underweight: number;
};

export interface AnalystRatingsSummary {
  aggregateRatings: RatingsAggregate | null;
  consensusPriceTarget: string | null;
  consensusRating: string | null;
  consensusRatingValue: number | null;
  highestPriceTarget: string | null;
  lowestPriceTarget: string | null;
  mostRecentFirms: { date: string; name: string }[];
  numRatings: number;
  numUniqueRatings: number;
  ratings: Ratings[];
  uniqueRatingsFromLastThreeYears: Ratings[];
  highestPriceRating: PricedRating | null;
  lowestPriceRating: PricedRating | null;
}

interface PricedRating {
  date: string;
  firm: string;
  price: string;
}
const sortByRecentDate = (data: Ratings[]) => {
  return data?.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
};

const getAggregateRatings = (ratings: Ratings[]): RatingsAggregate => {
  const aggregate = {
    Buy: 0,
    Hold: 0,
    Overweight: 0,
    Sell: 0,
    Underweight: 0,
  };
  ratings?.map((rating: Ratings) => {
    if (BuyRating.includes(rating.rating_current)) {
      aggregate['Buy']++;
    } else if (OverweightRating.includes(rating.rating_current)) {
      aggregate['Overweight']++;
    } else if (HoldRating.includes(rating.rating_current)) {
      aggregate['Hold']++;
    } else if (UnderweightRating.includes(rating.rating_current)) {
      aggregate['Underweight']++;
    } else if (SellRating.includes(rating.rating_current)) {
      aggregate['Sell']++;
    }
  });
  return aggregate;
};

const calculateConsensusRating = (ratings: string[]): number => {
  const ratingValues = {
    'Strong Sell': 1,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    Sell: 2,
    Underweight: 2.5,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    Neutral: 3,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    Hold: 3,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    Buy: 4,
    Outperform: 4.7,
    Overweight: 4.5,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    'Market Outperform': 4.8,
    'Strong Buy': 5,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    'Conviction Buy': 5,
  };

  const sumOfRatings = ratings?.reduce((totalRatings, rating) => {
    const ratingValue = ratingValues[rating];
    return totalRatings + (ratingValue ? ratingValue : 0);
  }, 0);

  const result = (sumOfRatings / (ratings.length * 5)) * 100;

  return result;
};

const getUniqueArray = (array: Ratings[], keysToFilter: string[]): Ratings[] => {
  return Object.values(
    array?.reduce((uniqueMap, entry) => {
      const key = keysToFilter.map(k => entry[k]).join('|');
      if (!(key in uniqueMap)) uniqueMap[key] = entry;
      return uniqueMap;
    }, {}),
  );
};

const getMostFrequent = (array: string[]): string => {
  const hashmap = array?.reduce((acc, val) => {
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(hashmap).reduce((a, b) => (hashmap[a] > hashmap[b] ? a : b));
};

const getThreeYearsAgo = (): number => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 3);
  return date.getTime();
};

const getMostRecentFirms = (ratings: Ratings[]): { date: string; name: string }[] => {
  const recentRatings = sortByRecentDate(ratings);
  const seen = new Set();
  const recentFirms: { date: string; name: string }[] = [];

  for (const rating of recentRatings) {
    const key = `${rating.date}-${rating.analyst}`;
    if (!seen.has(key)) {
      seen.add(key);
      recentFirms.push({ date: rating.date, name: rating.analyst });
    }
    if (recentFirms.length === 5) break;
  }

  return recentFirms;
};

const getSplitAdjustmentFactor = (ratingDate: string, splits: Splits[]): number => {
  const ratingTime = new Date(ratingDate).getTime();
  if (!splits?.length || Number.isNaN(ratingTime)) {
    return 1;
  }
  const now = Date.now();
  return splits.reduce((factor, split) => {
    const exTime = new Date(split?.date_ex).getTime();
    // Only splits that took effect after the rating and on/before today move the basis.
    if (Number.isNaN(exTime) || exTime <= ratingTime || exTime > now) {
      return factor;
    }
    const [numerator, denominator] = String(split?.ratio ?? '')
      .split(':')
      .map(Number);
    if (!numerator || !denominator || Number.isNaN(numerator) || Number.isNaN(denominator)) {
      return factor;
    }
    return factor * (denominator / numerator);
  }, 1);
};

const getSplitAdjustedPriceTarget = (rating: Ratings, splits: Splits[]): number =>
  Number(rating?.pt_current) * getSplitAdjustmentFactor(rating?.date, splits);

const OUTLIER_MEDIAN_MULTIPLE = 5;
const MIN_RATINGS_FOR_OUTLIER_GUARD = 4;

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const getDetailedData = (ratings: Ratings[], splits: Splits[] = []): AnalystRatingsSummary => {
  if (!ratings.length) {
    return {
      aggregateRatings: null,
      consensusPriceTarget: null,
      consensusRating: null,
      consensusRatingValue: null,
      highestPriceRating: null,
      highestPriceTarget: null,
      lowestPriceRating: null,
      lowestPriceTarget: null,
      mostRecentFirms: [],
      numRatings: ratings.length,
      numUniqueRatings: 0,
      ratings: [],
      uniqueRatingsFromLastThreeYears: [],
    };
  }

  const sortedByNewest = sortByRecentDate(ratings);
  const uniqueValuesArray: Ratings[] = getUniqueArray(sortedByNewest, ['analyst', 'analyst_name']);
  const threeYearsAgoInMiliseconds = getThreeYearsAgo();
  const uniqueRatingsFromLastThreeYears = uniqueValuesArray.filter(
    item => new Date(item.date).getTime() >= threeYearsAgoInMiliseconds && item.pt_current !== '',
  );

  if (!uniqueRatingsFromLastThreeYears.length) {
    return {
      aggregateRatings: null,
      consensusPriceTarget: null,
      consensusRating: null,
      consensusRatingValue: null,
      highestPriceRating: null,
      highestPriceTarget: null,
      lowestPriceRating: null,
      lowestPriceTarget: null,
      mostRecentFirms: [],
      numRatings: ratings.length,
      numUniqueRatings: uniqueValuesArray.length,
      ratings: sortedByNewest,
      uniqueRatingsFromLastThreeYears: [],
    };
  }

  const mostRecentFirms = getMostRecentFirms(uniqueRatingsFromLastThreeYears);

  const aggregateRatings = getAggregateRatings(uniqueRatingsFromLastThreeYears);

  // return {
  //   uniqueRatingsFromLastThreeYears,
  // };

  // Restate every in-window target on the current share basis before comparing or
  // averaging, so a firm's pre-split target is never mixed with post-split targets on a
  // ticker that split inside the window (fusion#16286). Sorted ascending by adjusted
  // price so the returned `uniqueRatingsFromLastThreeYears` keeps its price ordering.
  const adjustedRatings = uniqueRatingsFromLastThreeYears
    .map(rating => ({ price: getSplitAdjustedPriceTarget(rating, splits), rating }))
    .sort((a, b) => a.price - b.price);

  // Guard against source rows whose split adjustment was never applied (fusion#16286 defect
  // #2). With enough ratings for a stable median, drop high-side outliers above the median by
  // more than OUTLIER_MEDIAN_MULTIPLE; never drop everything.
  const cohortMedian = median(adjustedRatings.map(item => item.price));
  const guardedRatings =
    adjustedRatings.length >= MIN_RATINGS_FOR_OUTLIER_GUARD && cohortMedian > 0
      ? adjustedRatings.filter(item => item.price <= cohortMedian * OUTLIER_MEDIAN_MULTIPLE)
      : adjustedRatings;
  const effectiveRatings = guardedRatings.length ? guardedRatings : adjustedRatings;

  const lowestPriced = effectiveRatings[0];
  const highestPriced = effectiveRatings[effectiveRatings.length - 1];

  const highestPriceTarget = highestPriced.price.toFixed(2);
  const highestPricedFormatted = {
    date: highestPriced.rating?.date,
    firm: highestPriced.rating?.analyst,
    price: highestPriceTarget,
  };

  const lowestPriceTarget = lowestPriced.price.toFixed(2);
  const lowestPricedFormatted = {
    date: lowestPriced.rating?.date,
    firm: lowestPriced.rating?.analyst,
    price: lowestPriceTarget,
  };

  const priceTargetsArray = effectiveRatings.map(item => item.price);
  // The outlier guard only trims the PRICE-TARGET stats; a firm dropped for a bad pt_current
  // still has a valid rating stance, so the rating consensus uses the full in-window set
  // (consistent with aggregateRatings / mostRecentFirms above).
  const ratingArray = uniqueRatingsFromLastThreeYears.map(rating => rating.rating_current);
  const consensusPriceTarget = (
    priceTargetsArray.reduce((a, b) => a + b, 0) / priceTargetsArray.length
  ).toFixed(2);

  const consensusRating = getMostFrequent(ratingArray);
  const consensusRatingValue = calculateConsensusRating(ratingArray);

  return {
    aggregateRatings,
    consensusPriceTarget,
    consensusRating,
    consensusRatingValue,
    highestPriceRating: highestPricedFormatted,
    highestPriceTarget,
    lowestPriceRating: lowestPricedFormatted,
    lowestPriceTarget,
    mostRecentFirms,
    numRatings: ratings.length,
    numUniqueRatings: uniqueValuesArray.length,
    ratings: sortedByNewest,
    // Full in-window set, preserving the original date ordering — the price-target guard must
    // not remove a firm's otherwise-valid rating from the list/gauge; only the price stats
    // above are trimmed. (adjustedRatings is a price-sorted copy used only for the stats.)
    uniqueRatingsFromLastThreeYears,
  };
};
